import {
  PROTOTYPE_REGIONS,
  isActiveMajorPowerId,
  type ActiveMajorPowerId,
} from '../data/regions'
import { ACTIVE_MAJOR_POWER_IDS } from '../data/nations'
import { getFacilityDefinition } from '../data/facilities'
import { getNationProfile } from '../data/nationProfiles'
import type {
  NationCapacity,
  ResourceStockpile,
} from './economyTypes'
import type { RegionFacilityRecord } from './construction'

export interface NationEconomyState {
  stockpile: ResourceStockpile
  capacity: NationCapacity
  lastIncome: ResourceStockpile
}

export interface NationState {
  id: ActiveMajorPowerId
  economy: NationEconomyState
}

export type NationStateRecord = Record<ActiveMajorPowerId, NationState>
export type RegionControllerRecord = Record<string, ActiveMajorPowerId>

const STARTING_STOCKPILE: ResourceStockpile = {
  money: 120,
  metal: 55,
  fuel: 45,
  electronics: 30,
}

export function createInitialRegionControllers(): RegionControllerRecord {
  return Object.fromEntries(
    PROTOTYPE_REGIONS.map((region) => {
      if (!isActiveMajorPowerId(region.patronId)) {
        throw new Error(`Prototype region ${region.id} has an inactive patron`)
      }
      return [region.id, region.patronId]
    }),
  )
}

export function calculateNationOutput(
  nationId: ActiveMajorPowerId,
  controllers: RegionControllerRecord,
  facilities: RegionFacilityRecord,
): { income: ResourceStockpile; capacity: NationCapacity } {
  const income: ResourceStockpile = {
    money: 0,
    metal: 0,
    fuel: 0,
    electronics: 0,
  }
  const capacity: NationCapacity = {
    power: 0,
    research: 0,
  }
  const profile = getNationProfile(nationId)

  for (const region of PROTOTYPE_REGIONS) {
    if (controllers[region.id] !== nationId) {
      continue
    }

    income.money += region.production.money
    income.metal += region.production.metal
    income.fuel += region.production.fuel
    income.electronics += region.production.electronics
    capacity.power += region.capacity.power
    capacity.research += region.capacity.research

    for (const facilityId of facilities[region.id] ?? []) {
      const facility = getFacilityDefinition(facilityId)
      const modifier = profile.facilityModifiers[facilityId]

      income.money +=
        facility.output.money + (modifier?.output?.money ?? 0)
      income.metal +=
        facility.output.metal + (modifier?.output?.metal ?? 0)
      income.fuel +=
        facility.output.fuel + (modifier?.output?.fuel ?? 0)
      income.electronics +=
        facility.output.electronics +
        (modifier?.output?.electronics ?? 0)
      capacity.power +=
        facility.capacity.power + (modifier?.capacity?.power ?? 0)
      capacity.research +=
        facility.capacity.research +
        (modifier?.capacity?.research ?? 0)
    }
  }

  return { income, capacity }
}

export function createInitialNationStates(
  controllers: RegionControllerRecord,
  facilities: RegionFacilityRecord,
): NationStateRecord {
  return Object.fromEntries(
    ACTIVE_MAJOR_POWER_IDS.map((id) => [
      id,
      createNationState(id, controllers, facilities),
    ]),
  ) as NationStateRecord
}

export function resolveEconomyTurn(
  nations: NationStateRecord,
  controllers: RegionControllerRecord,
  facilities: RegionFacilityRecord,
): NationStateRecord {
  return Object.fromEntries(
    ACTIVE_MAJOR_POWER_IDS.map((id) => [
      id,
      resolveNationEconomy(nations[id], controllers, facilities),
    ]),
  ) as NationStateRecord
}

export function isNationStateRecord(value: unknown): value is NationStateRecord {
  if (!isRecord(value)) {
    return false
  }

  return ACTIVE_MAJOR_POWER_IDS.every((id) => isNationState(value[id], id))
}

export function isRegionControllerRecord(
  value: unknown,
): value is RegionControllerRecord {
  if (!isRecord(value)) {
    return false
  }

  return PROTOTYPE_REGIONS.every((region) => {
    const controller = value[region.id]
    return isActiveMajorPowerId(controller)
  })
}

function createNationState(
  id: ActiveMajorPowerId,
  controllers: RegionControllerRecord,
  facilities: RegionFacilityRecord,
): NationState {
  const { capacity } = calculateNationOutput(id, controllers, facilities)

  return {
    id,
    economy: {
      stockpile: { ...STARTING_STOCKPILE },
      capacity,
      lastIncome: {
        money: 0,
        metal: 0,
        fuel: 0,
        electronics: 0,
      },
    },
  }
}

function resolveNationEconomy(
  nation: NationState,
  controllers: RegionControllerRecord,
  facilities: RegionFacilityRecord,
): NationState {
  const { income, capacity } = calculateNationOutput(
    nation.id,
    controllers,
    facilities,
  )

  return {
    ...nation,
    economy: {
      stockpile: {
        money: nation.economy.stockpile.money + income.money,
        metal: nation.economy.stockpile.metal + income.metal,
        fuel: nation.economy.stockpile.fuel + income.fuel,
        electronics:
          nation.economy.stockpile.electronics + income.electronics,
      },
      capacity,
      lastIncome: income,
    },
  }
}

function isNationState(
  value: unknown,
  expectedId: ActiveMajorPowerId,
): value is NationState {
  if (!isRecord(value) || value.id !== expectedId || !isRecord(value.economy)) {
    return false
  }

  return (
    isResourceStockpile(value.economy.stockpile) &&
    isNationCapacity(value.economy.capacity) &&
    isResourceStockpile(value.economy.lastIncome)
  )
}

function isResourceStockpile(value: unknown): value is ResourceStockpile {
  if (!isRecord(value)) {
    return false
  }

  return (
    isNonNegativeNumber(value.money) &&
    isNonNegativeNumber(value.metal) &&
    isNonNegativeNumber(value.fuel) &&
    isNonNegativeNumber(value.electronics)
  )
}

function isNationCapacity(value: unknown): value is NationCapacity {
  if (!isRecord(value)) {
    return false
  }

  return (
    isNonNegativeNumber(value.power) &&
    isNonNegativeNumber(value.research)
  )
}

function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}
